// src/pages/Login.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Paper,
  Avatar,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Box,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Fade,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LanguageIcon from "@mui/icons-material/Language";

import { useAuth } from '../../context/AuthContext'; // ✅ Import Auth Hook
import ForgotPasswordDialog from "./ForgotPasswordDialog";

export default function Login() {
  const navigate = useNavigate();
  const { setAuthData } = useAuth(); // ✅ Get setAuthData from context
  const { t, i18n } = useTranslation();
  const [password, setPassword] = useState("");
  const [mobile, setmobile]=useState("");
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("error");
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const savedMobile = localStorage.getItem("rememberedMobile");
    if (savedMobile) {
      setmobile(savedMobile);
      setRememberMe(true);
    }
  }, []);

  const handleLanguageClick = (event) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLanguageClose();
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage(null);

  try {
    const response = await axios.post("/User/login", {
      mobile: mobile.trim(),
      password: password.trim(),
    });

    // ✅ قراءة موحدة
    const {
      success,
      message,
      token,
      companyId,
      branchId,
      permissions = []
    } = response.data;

    // ❌ فشل منطقي من السيرفر
    if (!success) {
      throw new Error(message || "❌ فشل تسجيل الدخول");
    }

    // ✅ حفظ البيانات
    localStorage.setItem("token", token);
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("branchId", branchId);
    
    if (rememberMe) {
      localStorage.setItem("rememberedMobile", mobile.trim());
    } else {
      localStorage.removeItem("rememberedMobile");
    }

    if (response.data.safeId) localStorage.setItem("safeId", response.data.safeId);
    else localStorage.removeItem("safeId");

    if (response.data.stockId) localStorage.setItem("stockId", response.data.stockId);
    else localStorage.removeItem("stockId");

    // Multiple Allow Lists
    if (response.data.allowedSafeIds && response.data.allowedSafeIds.length > 0) {
        localStorage.setItem("allowedSafeIds", JSON.stringify(response.data.allowedSafeIds));
    } else {
        localStorage.removeItem("allowedSafeIds");
    }

    if (response.data.allowedStockIds && response.data.allowedStockIds.length > 0) {
        localStorage.setItem("allowedStockIds", JSON.stringify(response.data.allowedStockIds));
    } else {
        localStorage.removeItem("allowedStockIds");
    }

    if (response.data.userId) {
        localStorage.setItem("userId", response.data.userId);
    }

    if (response.data.userName) {
        localStorage.setItem("userName", response.data.userName);
    }

    if (response.data.companyName) {
        localStorage.setItem("companyName", response.data.companyName);
    }

    if (response.data.mobile) {
        localStorage.setItem("mobile", response.data.mobile);
    }

    setAuthData({ permissions });

    setSeverity("success");
    setMessage(message || `✅ ${t('login.messages.loginSuccess')}`);

    setTimeout(() => navigate("/dashboard"), 800);

  } catch (err) {
    // ✅ إظهار رسالة السيرفر الحقيقية
    const serverMsg =
      err.response?.data?.message ||
      err.message ||
      `❌ ${t('login.messages.loginFailed')}`;

    setSeverity("error");
    setMessage(serverMsg);
  }
};


  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.15) 0%, transparent 40%),
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 100% 100%, 40px 40px, 40px 40px',
          zIndex: 1,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '200%',
          height: '200%',
          top: '-50%',
          left: '-50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.01) 0%, transparent 70%)',
          animation: 'rotate 60s linear infinite',
          zIndex: 0,
        },
        '@keyframes rotate': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        }
      }}
    >
      <Fade in timeout={1000}>
        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: 4,
              width: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Inner Language Switcher */}
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <IconButton
                onClick={handleLanguageClick}
                size="small"
                sx={{ 
                  color: 'primary.main',
                  bgcolor: 'rgba(0,0,0,0.03)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
              >
                <LanguageIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={handleLanguageClose}
                TransitionComponent={Fade}
              >
                <MenuItem onClick={() => changeLanguage('ar')} sx={{ fontWeight: 'bold' }}>
                  {t('common.arabic')}
                </MenuItem>
                <MenuItem onClick={() => changeLanguage('en')} sx={{ fontWeight: 'bold' }}>
                  {t('common.english')}
                </MenuItem>
              </Menu>
            </Box>

            <Avatar 
              sx={{ 
                m: 1, 
                bgcolor: "primary.main", 
                width: 64, 
                height: 64,
                boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.5)'
              }}
            >
              <LockOutlinedIcon fontSize="large" />
            </Avatar>
            
            <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 900, color: 'primary.dark', letterSpacing: -0.5 }}>
              {t('login.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontWeight: 500 }}>
              {t('login.welcome_subtitle') || 'نظام بروفي المتكامل'}
            </Typography>

            {message && (
              <Alert 
                severity={severity} 
                variant="filled"
                sx={{ width: "100%", mb: 3, borderRadius: 2 }}
              >
                {message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              <TextField
                margin="normal"
                fullWidth
                label={t('login.mobile')}
                value={mobile}
                onChange={(e) => setmobile(e.target.value)}
                required
                autoFocus
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: 'white' }
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                fullWidth
                label={t('login.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: 'white' }
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)} 
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      {t('login.remember_me') || 'تذكرني'}
                    </Typography>
                  }
                />
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ 
                    fontWeight: 700, 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { color: 'primary.dark' }
                  }}
                >
                  {t('common.forgot_password')}
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ 
                  py: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 900,
                  mb: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
                  '&:hover': {
                    boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {t('login.loginButton')}
              </Button>

              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('login.noAccount')}{" "}
                  <Link 
                    component={RouterLink} 
                    to="/register" 
                    sx={{ 
                      fontWeight: 800, 
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' } 
                    }}
                  >
                    {t('login.createAccount')}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Fade>
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onClose={() => setForgotPasswordOpen(false)} 
      />
    </Box>
  );
}
